import { Box, CircularProgress, ToggleButton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { AREA_COLOR_MAP } from "@/shared/colors";
import {
	GroupedDelayAsset,
	getDelayAssetsGrouped,
} from "@/shared/repositories/DelayAssetRepository";
import { useUserScope } from "@/setup/scope";

type Props = {
	areaAssetId: number;
	value: number | null;
	onChange: (assetId: number | null) => void;
};

export const LineSelector = ({ areaAssetId, value, onChange }: Props) => {
	const { data: assets, isLoading } = useQuery<GroupedDelayAsset[], Error>({
		queryKey: ["delayAssetsGrouped"],
		queryFn: () => getDelayAssetsGrouped(),
	});
	const scope = useUserScope();

	const lines = useMemo(
		() =>
			(assets ?? [])
				.filter(
					(a) => a.ParentAssetId === areaAssetId && scope.includes(a.AssetId),
				)
				.sort((a, b) => a.AssetName.localeCompare(b.AssetName)),
		[assets, areaAssetId, scope],
	);

	// Single line: preselect
	useEffect(() => {
		if (lines.length === 1 && value !== lines[0].AssetId)
			onChange(lines[0].AssetId);
	}, [lines, value]);

	if (isLoading) return <CircularProgress size={24} />;

	const color = AREA_COLOR_MAP[areaAssetId];

	// Mobile-first: buttons fill the row and wrap on phone, natural row from sm up.
	return (
		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
			{lines.map((line) => (
				<ToggleButton
					key={line.AssetId}
					value={line.AssetId}
					selected={value === line.AssetId}
					onChange={() => onChange(line.AssetId)}
					sx={{
						flex: "1 1 auto",
						minWidth: 0,
						px: 1.25,
						py: 1.25,
						minHeight: 48,
						fontWeight: 600,
						textTransform: "none",
						whiteSpace: "nowrap",
						borderRadius: 2,
						"&.Mui-selected, &.Mui-selected:hover": {
							backgroundColor: color,
							borderColor: color,
							color: "#fff",
						},
					}}
				>
					{line.AssetName}
				</ToggleButton>
			))}
		</Box>
	);
};
